import { Outlet } from 'react-router';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { OfflineBanner } from '@/components/shared/OfflineBanner';
import { AutoCloseBanner } from '@/features/attendance';
import { useVersionCheck } from '@/app/useVersionCheck';
import { PushEnrollmentBanner } from '@/features/settings/PushEnrollmentBanner';
import { usePushAutoEnroll } from '@/features/settings/usePushAutoEnroll';

export interface ShellProject {
  key: string;
  name: string;
  icon: string;
}

export function AppShell({ projects }: { projects?: ShellProject[] }) {
  // Prompts to refresh when the server is serving a build this tab is not
  // running — the stale-service-worker class (V5-02 §2.4).
  useVersionCheck();
  // V7-01 §2.2 — zero clicks forever after. Re-subscribes silently whenever
  // permission is already granted but the subscription has gone stale.
  usePushAutoEnroll();

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[var(--bg)]">
      <OfflineBanner />
      <TopBar />
      <AutoCloseBanner />
      {/* V7-01 §2 — the prompt comes to the person rather than waiting in a
          settings screen to be found. Renders nothing at all when it should
          not be asking; the cadence lives in `pushBannerState.ts`. */}
      <PushEnrollmentBanner />
      <div className="flex min-h-0 flex-1">
        <Sidebar projects={projects} />
        <main className="min-w-0 flex-1 overflow-y-auto">
          {/* Keyed on nothing: a crash in one route must not blank the shell. */}
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

/** Standard page header: title, actions, then a filter/toolbar slot (§8.2). */
export function PageHeader({
  title,
  subtitle,
  actions,
  toolbar,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
}) {
  return (
    <div className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-[20px] font-semibold">{title}</h1>
          {subtitle ? <p className="truncate text-[12px] vt-muted">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
      </div>
      {toolbar ? <div className="px-4 pb-2">{toolbar}</div> : null}
    </div>
  );
}
